import { useEffect, useMemo, useState } from 'react';
import Icon from './Icon';
import { fetchMemberCommittees } from '../memberCommitteesApi';
import { committeeMemberRoleLabel, committeeTypeLabel } from '../committeeFormUtils';
import { useLanguage } from '../i18n/language';
import { useT } from '../i18n/translations';
import './BranchesPage.css';
import './BoardPage.css';

export default function MemberCommitteesPage() {
  const { language, direction } = useLanguage();
  const t = useT();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchMemberCommittees();
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : t('committees.loadFailed'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((x) => {
      const mq = !q || x.name.toLowerCase().includes(q) || (x.chairmanName || '').toLowerCase().includes(q);
      const mt = typeFilter === 'all' || x.type === typeFilter;
      return mq && mt;
    });
  }, [rows, search, typeFilter]);

  return (
    <div className="page" dir={direction}>
      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title"><Icon name="board" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} /> {t('committees.title')}</span>
            <span className="list-page-card__sub">{t('committees.memberSubtitle')}</span>
          </div>
        </header>
        {error && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t('committees.loading')}</div>}
        {!loading && (
        <>
          <div className="page__filters">
            <div className="page__search">
              <Icon name="search" size={14} color="#90a4ae" className="page__search-icon" />
              <input className="page__search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('committees.memberSearch')} />
            </div>
            <select className="page__filter-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="all">{t('committees.allTypes')}</option>
              <option value="permanent">{committeeTypeLabel('permanent', language)}</option>
              <option value="temporary">{committeeTypeLabel('temporary', language)}</option>
            </select>
          </div>
          <div className="table-card">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('committees.colName')}</th>
                  <th>{t('committees.colType')}</th>
                  <th>{t('committees.colChairman')}</th>
                  <th>{t('committees.colMembers')}</th>
                  <th>{t('committees.colTopMembers')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('committees.colCreatedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr key={row.id}>
                    <td><strong style={{ color: '#00334a' }}>{row.name}</strong></td>
                    <td>{committeeTypeLabel(row.type, language)}</td>
                    <td>{row.chairmanName || '—'}</td>
                    <td>{row.membersCount}</td>
                    <td>{(row.members || []).slice(0, 2).map((m) => `${m.boardMemberName} (${committeeMemberRoleLabel(m.role, language)})`).join('، ') || '—'}</td>
                    <td><span className={`users-status users-status--${row.isActive ? 'active' : 'inactive'}`}>{row.isActive ? t('committees.active') : t('committees.inactive')}</span></td>
                    <td>{row.createdAt || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && <div className="users-empty">{t('committees.noResults')}</div>}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
